import { useState, useEffect, useCallback, useMemo } from "react";
import { Chart, Legend } from "./components/IsoChart";
import { DensityChart, CumulativeChart } from "./components/DensityChart";
import { fetchEventData, fetchPopularEvents, isByDateEvent } from "./polymarket";
import type { MarketWithHistory } from "./types";
import type { EventSummary } from "./polymarket";
import "./index.css";

function getSlugFromPath(): string | null {
  const match = window.location.pathname.match(/^\/event\/(.+)/);
  return match?.[1] ?? null;
}

function HomePage() {
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPopularEvents()
      .then(setEvents)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="dashboard">
      <header>
        <h1>Polymarket History</h1>
        <p className="subtitle">Price history charts</p>
      </header>
      <div className="event-list">
        {loading && (
          <div className="status">
            <div className="spinner" />
            Loading...
          </div>
        )}
        {events.map((e) => (
          <a key={e.slug} href={`/event/${e.slug}`} className="event-card">
            <span className="event-title">{e.title}</span>
            <span className="event-meta">{e.marketCount} markets</span>
          </a>
        ))}
      </div>
    </div>
  );
}

export function App() {
  const [markets, setMarkets] = useState<MarketWithHistory[]>([]);
  const [title, setTitle] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const slug = getSlugFromPath();
  const isByDate = useMemo(
    () => isByDateEvent(markets.map((m) => m.info.question)),
    [markets]
  );

  const { tMin, tMax } = useMemo(() => {
    let min = Infinity, max = -Infinity;
    for (const m of markets) {
      for (const pt of m.history) {
        if (pt.t < min) min = pt.t;
        if (pt.t > max) max = pt.t;
      }
    }
    return { tMin: min === Infinity ? 0 : min, tMax: max === -Infinity ? 0 : max };
  }, [markets]);

  const [asOf, setAsOf] = useState<number | null>(null);

  // Reset slider when markets change
  useEffect(() => setAsOf(null), [markets]);

  const loadEvent = useCallback((eventSlug: string) => {
    setLoading(true);
    setError(null);
    fetchEventData(eventSlug)
      .then((data) => {
        setTitle(data.title);
        setMarkets(data.markets);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    loadEvent(slug);
  }, [slug, loadEvent]);

  if (!slug) return <HomePage />;

  return (
    <div className="dashboard">
      <header>
        <div className="header-row">
          <h1>{title || slug}</h1>
          <div className="header-actions">
            <a href="/" className="btn">Back</a>
            <button className="btn" onClick={() => loadEvent(slug)} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
        <p className="subtitle">
          Polymarket probability history
          {" · "}
          <a
            href={`https://polymarket.com/event/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View on Polymarket
          </a>
        </p>
      </header>

      {loading && (
        <div className="status">
          <div className="spinner" />
          Loading market data...
        </div>
      )}

      {error && <div className="status error">Error: {error}</div>}

      {!loading && !error && markets.length === 0 && (
        <div className="status">No markets found for this event.</div>
      )}

      {!loading && !error && markets.length > 0 && (
        <>
          <div className="chart-container">
            <Chart markets={markets} />
          </div>
          {isByDate && (
            <div className="time-slider">
              <label>
                {asOf
                  ? new Date(asOf * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                  : "Latest"}
              </label>
              <input
                type="range"
                min={tMin}
                max={tMax}
                value={asOf ?? tMax}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setAsOf(v >= tMax ? null : v);
                }}
              />
            </div>
          )}
          {isByDate && <DensityChart markets={markets} asOf={asOf ?? undefined} />}
          {isByDate && <CumulativeChart markets={markets} asOf={asOf ?? undefined} />}
          <Legend markets={markets} />
        </>
      )}
    </div>
  );
}

export default App;
